import type { ClientErrorGroup, ClientErrorsReport } from "@scoach/types";
import { useCallback, useEffect, useState } from "react";

import { adminApi } from "../api.ts";

type Source = "prod" | "qc" | "ads" | "current";

const KIND_LABEL: Record<string, string> = {
  react: "UI crash",
  window: "JS error",
  unhandledrejection: "Promise",
  api: "API/network",
};
const KIND_COLOR: Record<string, string> = {
  react: "#d93025",
  window: "#e37400",
  unhandledrejection: "#a142f4",
  api: "#1a73e8",
};

function timeAgo(iso: string): string {
  const d = Date.parse(iso);
  if (Number.isNaN(d)) return "";
  const s = Math.max(0, (Date.now() - d) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "6px 10px",
  fontSize: 12,
  color: "var(--text-3)",
  borderBottom: "1px solid var(--border-soft)",
  whiteSpace: "nowrap",
};
const td: React.CSSProperties = {
  padding: "8px 10px",
  fontSize: 13,
  borderBottom: "1px solid var(--border-soft)",
  verticalAlign: "top",
};

function BugRow({
  g,
  source,
  onResolvedChange,
}: {
  g: ClientErrorGroup;
  source: Source;
  onResolvedChange: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function toggleResolved() {
    setBusy(true);
    try {
      await adminApi.resolveClientError(g.signature, !g.resolved, source);
      onResolvedChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <tr style={{ opacity: g.resolved ? 0.5 : 1 }}>
        <td style={td}>
          <span
            style={{
              display: "inline-block",
              padding: "1px 7px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              color: "#fff",
              background: KIND_COLOR[g.kind] ?? "#5f6368",
            }}
          >
            {KIND_LABEL[g.kind] ?? g.kind}
          </span>
        </td>
        <td style={{ ...td, maxWidth: 420 }}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              textAlign: "left",
              color: "var(--text-1)",
              fontSize: 13,
              fontWeight: 600,
            }}
            title="Show details"
          >
            {open ? "▾ " : "▸ "}
            {g.title}
          </button>
          {g.routes.length > 0 && (
            <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 2 }}>
              on {g.routes.slice(0, 3).join(", ")}
              {g.routes.length > 3 ? " …" : ""}
            </div>
          )}
        </td>
        <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{g.count}</td>
        <td style={{ ...td, textAlign: "right" }}>{g.usersAffected}</td>
        <td style={{ ...td, whiteSpace: "nowrap", color: "var(--text-3)" }}>
          {timeAgo(g.lastSeen)}
        </td>
        <td style={{ ...td, textAlign: "right" }}>
          <button
            type="button"
            className="pill-btn"
            disabled={busy}
            onClick={() => void toggleResolved()}
          >
            {g.resolved ? "Reopen" : "Resolve"}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} style={{ ...td, background: "var(--surface-2, #f6f8fc)" }}>
            {g.sampleStack && (
              <pre
                style={{
                  margin: "0 0 10px",
                  padding: 10,
                  borderRadius: 8,
                  background: "var(--surface, #fff)",
                  border: "1px solid var(--border-soft)",
                  fontSize: 11.5,
                  overflowX: "auto",
                  maxHeight: 240,
                }}
              >
                {g.sampleStack}
              </pre>
            )}
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>
              First seen {timeAgo(g.firstSeen)} · Recent:
            </div>
            <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 12 }}>
              {g.samples.map((s) => (
                <li key={`${s.email}-${s.at}`}>
                  <strong>{s.email ?? "unknown"}</strong> — {new Date(s.at).toLocaleString()}
                  {s.route ? ` · ${s.route}` : ""}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

/** Admin report of the client-side errors users are hitting (defaults to prod). */
export function ClientErrorsSection() {
  const [report, setReport] = useState<ClientErrorsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [source, setSource] = useState<Source>("prod");
  const [showResolved, setShowResolved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    adminApi
      .fetchClientErrors(days, source)
      .then(setReport)
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [days, source]);

  useEffect(load, [load]);

  const groups = (report?.groups ?? []).filter((g) => showResolved || !g.resolved);

  return (
    <section style={{ marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700, flex: 1 }}>
          User bugs {report ? `(${source})` : ""}
        </h2>
        <select
          className="task-edit-input"
          value={source}
          onChange={(e) => setSource(e.target.value as Source)}
          title="Which environment's bugs to show"
        >
          <option value="prod">Production</option>
          <option value="qc">QC</option>
          <option value="ads">Ads</option>
          <option value="current">This env</option>
        </select>
        <select
          className="task-edit-input"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={1}>Last 24h</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <button type="button" className="pill-btn" onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      <p style={{ fontSize: 13, color: "var(--text-3)", marginTop: 4 }}>
        Real errors users hit in the browser — UI crashes, uncaught JS errors, unhandled promise
        rejections, and failed API/network calls — grouped by bug. Captured automatically once this
        version is running in the selected environment.
      </p>

      {error && (
        <div style={{ color: "var(--gc-red,#d93025)", fontSize: 13, marginTop: 8 }}>{error}</div>
      )}

      {report && (
        <div style={{ display: "flex", gap: 24, margin: "10px 0 6px", flexWrap: "wrap" }}>
          <Stat label="Unique bugs" value={report.uniqueBugs} />
          <Stat label="Total occurrences" value={report.totalErrors} />
          <Stat label="Users affected" value={report.usersAffected} />
          <label style={{ fontSize: 12.5, color: "var(--text-3)", alignSelf: "center" }}>
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
              style={{ marginRight: 6 }}
            />
            Show resolved
          </label>
        </div>
      )}

      {!loading && groups.length === 0 && (
        <div className="tasks-empty" style={{ marginTop: 8 }}>
          No bugs recorded in this window. 🎉 (If this version isn't deployed to the selected
          environment yet, nothing will be captured there.)
        </div>
      )}

      {groups.length > 0 && (
        <div style={{ overflowX: "auto", marginTop: 8 }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={th}>Type</th>
                <th style={th}>Bug</th>
                <th style={{ ...th, textAlign: "right" }}>Count</th>
                <th style={{ ...th, textAlign: "right" }}>Users</th>
                <th style={th}>Last seen</th>
                <th style={{ ...th, textAlign: "right" }}> </th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <BugRow key={g.signature} g={g} source={source} onResolvedChange={load} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "var(--font-display)" }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: "var(--text-3)" }}>{label}</div>
    </div>
  );
}
